package io.jsonize.web.components

import androidx.compose.material3.IconButton
import androidx.compose.material3.IconButtonColors
import androidx.compose.material3.IconButtonDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.Modifier
import androidx.compose.ui.input.pointer.PointerEventType
import androidx.compose.ui.input.pointer.onPointerEvent
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.layout.positionInWindow
import kotlinx.browser.document
import kotlinx.browser.window
import io.jsonize.web.services.logButtonClick
import org.w3c.dom.HTMLElement
import org.w3c.dom.TouchEvent
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlin.math.max
import kotlin.math.min

private const val TOOLTIP_ID = "jsonize-tooltip"

private data class TooltipAnchor(
    val x: Double,
    val y: Double,
    val width: Double,
    val height: Double
)

@OptIn(ExperimentalComposeUiApi::class)
@Composable
fun TooltipIconButton(
    tooltip: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    colors: IconButtonColors = IconButtonDefaults.iconButtonColors(),
    content: @Composable () -> Unit
) {
    var anchor by remember { mutableStateOf<TooltipAnchor?>(null) }
    var hideJob by remember { mutableStateOf<Job?>(null) }
    val scope = rememberCoroutineScope()

    val tooltipModifier = if (tooltip.isBlank()) {
        Modifier
    } else {
        Modifier
            .onGloballyPositioned { coords ->
                val pos = coords.positionInWindow()
                anchor = TooltipAnchor(
                    x = pos.x.toDouble(),
                    y = pos.y.toDouble(),
                    width = coords.size.width.toDouble(),
                    height = coords.size.height.toDouble()
                )
            }
            .onPointerEvent(PointerEventType.Enter) {
                anchor?.let { showHtmlTooltip(tooltip, it) }
            }
            .onPointerEvent(PointerEventType.Exit) {
                hideHtmlTooltip()
            }
            .pointerInput(tooltip) {
                awaitPointerEventScope {
                    while (true) {
                        val event = awaitPointerEvent()
                        val hasTouch = event.changes.any { change ->
                            (change.type == null) && (event.nativeEvent is TouchEvent)
                        }
                        if (hasTouch) {
                            anchor?.let { showHtmlTooltip(tooltip, it) }
                            hideJob?.cancel()
                            hideJob = scope.launch {
                                delay(1500)
                                hideHtmlTooltip()
                            }
                        }
                    }
                }
            }
    }

    IconButton(
        onClick = {
            hideHtmlTooltip()
            logButtonClick(if (tooltip.isNotBlank()) tooltip else "icon_button")
            onClick()
        },
        enabled = enabled,
        colors = colors,
        modifier = modifier.then(tooltipModifier)
    ) {
        content()
    }

    DisposableEffect(Unit) {
        onDispose {
            hideJob?.cancel()
            hideHtmlTooltip()
        }
    }
}

private fun showHtmlTooltip(text: String, anchor: TooltipAnchor) {
    if (text.isBlank()) return
    val tooltip = ensureTooltipElement()
    tooltip.textContent = text
    tooltip.classList.add("is-visible")
    tooltip.setAttribute("aria-hidden", "false")
    window.requestAnimationFrame {
        positionTooltip(tooltip, anchor)
    }
}

private fun positionTooltip(tooltip: HTMLElement, anchor: TooltipAnchor) {
    val rect = tooltip.getBoundingClientRect()
    val gap = 10.0
    val viewportWidth = window.innerWidth.toDouble()
    val viewportHeight = window.innerHeight.toDouble()

    var left = anchor.x + (anchor.width - rect.width) / 2.0
    left = left.coerceIn(gap, max(gap, viewportWidth - rect.width - gap))

    var top = anchor.y - rect.height - gap
    if (top < gap) {
        top = anchor.y + anchor.height + gap
        top = min(top, max(gap, viewportHeight - rect.height - gap))
    }

    tooltip.style.left = "${left}px"
    tooltip.style.top = "${top}px"
}

private fun hideHtmlTooltip() {
    val tooltip = document.getElementById(TOOLTIP_ID) as? HTMLElement ?: return
    tooltip.classList.remove("is-visible")
    tooltip.setAttribute("aria-hidden", "true")
}

private fun ensureTooltipElement(): HTMLElement {
    val existing = document.getElementById(TOOLTIP_ID) as? HTMLElement
    if (existing != null) return existing

    val tooltip = document.createElement("div") as HTMLElement
    tooltip.id = TOOLTIP_ID
    tooltip.className = "jsonize-tooltip"
    tooltip.setAttribute("role", "tooltip")
    tooltip.setAttribute("aria-hidden", "true")
    document.body?.appendChild(tooltip)
    return tooltip
}
